import { clip, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, outCubic, outExpo, outQuad, phase, settle } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';
import { sentinelClips } from './sentinel.ts';

/*
 * Prior Lucan (models/lucan.ts, docs/ANTIHEROES.md): on the fallen sentinel's bones like Ser Corvin, so he walks,
 * reels, staggers and falls as it does. His own: the bolt (attack: the crozier thrust at a hero, landing at 0.13 s),
 * the vigil whip (the censer swung round on its chain), raising the fallen (both arms lifted, palms up), the channel
 * of the Empty Chapel (the crozier held high, his head thrown back in prayer) and the Chapel itself (the crozier
 * driven into the ground, 0.2 s).
 *
 * Conventions as in sentinel.ts: −X on an arm swings it forward and up, −X on a forearm bends the elbow, +X on
 * the trunk leans it forward, +Y turns the trunk to its left.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'flapF' | 'flapB'
  | 'armL' | 'foreL' | 'shield' | 'armR' | 'foreR' | 'sword'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** The bolt: the crozier drawn back, then thrust at the hero, the trunk turned into it. */
const attack = (): Clip<B> =>
  clip<B>('attack', 1.1, false, (b, t) => {
    const back: P = { body: [-0.04, 0.2, 0], chest: [-0.1, 0.3, 0], head: [0, -0.2, 0], armL: [-0.6, 0, 0.2], foreL: [-0.9, 0, 0], shield: [0.3, 0, 0], armR: [0.2, 0, -0.1] };
    const thrust: P = { root: [0, -0.04, 0, 0, -0.02, 0.06], body: [0.1, -0.15, 0], chest: [0.15, -0.3, 0], head: [0.05, 0.2, 0], armL: [-1.45, 0, -0.05], foreL: [-0.1, 0, 0], shield: [-1.1, 0, 0], armR: [0.3, 0, -0.15], legL: [-0.3, 0, 0], shinL: [0.25, 0, 0], legR: [0.2, 0, 0], flapF: [-0.15, 0, 0] };
    timeline(b, t, [
      [0, back, outQuad],
      [0.13, thrust, inCubic],
      [0.45, thrust, outCubic],
      [1.1, {}, inOut],
    ], { head: 0.04, flapF: 0.08, flapB: 0.08 });
  }, (t) => thud(t, [[0.13, 0.03]]), 'Saeta');

/** The vigil whip: the censer swung out and round on its chain, hard across in front of him. */
const whip = (): Clip<B> =>
  clip<B>('whip', 1.2, false, (b, t) => {
    const wind: P = { body: [0, -0.3, 0], chest: [-0.05, -0.45, 0], armR: [-1.2, 0, -0.9], foreR: [-0.2, 0, 0], sword: [-0.6, 0, 0], armL: [0.2, 0, 0.1] };
    const lash: P = { root: [0, -0.05, 0, 0, -0.03, 0.08], body: [0.1, 0.35, 0], chest: [0.15, 0.55, 0], head: [0.05, -0.3, 0], armR: [-1.5, 0, 0.7], foreR: [-0.05, 0, 0], sword: [-1.4, 0, 0], armL: [0.3, 0, 0.2], legL: [-0.35, 0, 0], legR: [0.25, 0, 0], flapF: [-0.25, 0, 0], flapB: [0.2, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.25, wind, outQuad],
      [0.42, lash, inCubic],
      [0.7, lash],
      [1.2, {}, inOut],
    ], { head: 0.05, sword: 0.1, flapF: 0.08, flapB: 0.08 });
  }, undefined, 'Látigo de vigilia');

/** Raising the fallen: both arms lifted from low to high, palms up, as if pulling them out of the ground. */
const raise = (): Clip<B> =>
  clip<B>('raise', 1.6, false, (b, t) => {
    const low: P = { root: [0, -0.06, 0, 0, -0.06, 0], body: [0.3, 0, 0], chest: [0.3, 0, 0], head: [0.25, 0, 0], armL: [-0.4, 0, 0.45], foreL: [-0.2, 0, 0], armR: [-0.4, 0, -0.45], foreR: [-0.2, 0, 0], legL: [-0.3, 0, -0.1], shinL: [0.5, 0, 0], legR: [-0.3, 0, 0.1], shinR: [0.5, 0, 0] };
    const high: P = { root: [0, 0.02, 0, 0, 0.02, 0], body: [-0.12, 0, 0], chest: [-0.2, 0, 0], head: [-0.35, 0, 0], armL: [-2.4, 0, 0.5], foreL: [-0.15, 0, 0], shield: [0.4, 0, 0], armR: [-2.4, 0, -0.5], foreR: [-0.15, 0, 0], sword: [0.3, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.35, low, outCubic],
      [0.75, high, outExpo],
      [1.15, high],
      [1.6, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
  }, (t) => thud(t, [[0.75, 0.03]]), 'Levantar a los caídos');

/** The Empty Chapel, channelled: the crozier held high, the censer swinging slow, his head thrown back. */
const channel = (): Clip<B> =>
  clip<B>('channel', 1.4, true, (b, t) => {
    const p = phase(t, 1.4) * TAU;
    const shake = Math.sin(p * 6) * 0.025;
    b.root.pos(0, 0.03 + 0.015 * Math.sin(p), 0).rot(-0.04, 0, 0);
    b.body.rot(-0.08, 0, 0);
    b.chest.rot(-0.2 + shake, 0, 0);
    b.head.rot(-0.5, 0, 0);
    b.armL.rot(-2.75 + shake, 0, 0.1);
    b.foreL.rot(-0.1);
    b.shield.rot(0.2);
    b.armR.rot(-0.9, 0, -0.5 + 0.1 * Math.sin(p));
    b.foreR.rot(-0.3);
    b.sword.rot(0.5 * Math.sin(p), 0, 0.3 * Math.cos(p));
    b.legL.rot(-0.05, 0, -0.06);
    b.legR.rot(0.05, 0, 0.06);
    b.flapF.rot(0.08 * Math.sin(p * 2));
    b.flapB.rot(-0.1 + 0.08 * Math.sin(p * 2 + 1));
  }, (t) => {
    const k = Math.sin(phase(t, 1.4) * TAU * 6) * 0.005;
    return [1 + k, 1 - k, 1 + k];
  }, 'Canaliza');

/** The Chapel lands: from high, the crozier driven down into the ground before him. */
const chapel = (): Clip<B> =>
  clip<B>('chapel', 1.7, false, (b, t) => {
    const high: P = { chest: [-0.2, 0, 0], head: [-0.5, 0, 0], armL: [-2.75, 0, 0.1], foreL: [-0.1, 0, 0], shield: [0.2, 0, 0], armR: [-0.9, 0, -0.5], foreR: [-0.3, 0, 0] };
    const down: P = { root: [0, 0, 0, 0, -0.18, 0.12], body: [0.3, 0, 0], chest: [0.35, 0, 0], head: [0.15, 0, 0], armL: [-0.9, 0, 0.1], foreL: [-0.2, 0, 0], shield: [0.1, 0, 0], armR: [-0.5, 0, -0.4], foreR: [-0.4, 0, 0], legL: [-0.6, 0, -0.05], shinL: [0.8, 0, 0], legR: [0.4, 0, 0.05], shinR: [0.5, 0, 0], flapF: [-0.4, 0, 0], flapB: [0.35, 0, 0] };
    timeline(b, t, [
      [0, high],
      [0.2, down, inCubic],
      [0.9, down],
      [1.7, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0.015 * kick(t, 0.2, 7, 6), 0, 0);
  }, (t) => thud(t, [[0.2, 0.08]]), 'La Capilla Vacía');

/** Clips by library kind (see ../rigs.ts): the sentinel's, with his own blow and powers in place of the bash. */
export const lucanClips = (): Clip[] => [...sentinelClips().filter((c) => c.name !== 'attack'), attack(), whip(), raise(), channel(), chapel()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { lucan: lucanClips };
